import { z } from 'zod'

import { EnumBuilder, type ZodSimplify } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { CountSchema } from './property.models.ts'
import {
  ProjectIdSchema,
  ProjectServiceIdSchema,
  ReviewIdSchema,
  ReviewUrnSchema,
} from './unique-id.models.ts'

/**
 * ReviewType
 */
export const [ReviewTypes, ReviewTypeSchema, ReviewTypeEnum] = EnumBuilder(
  'pre_production',
  'post_production'
)
export type ReviewType = ZodSimplify<typeof ReviewTypeSchema>

/**
 * ReviewKey
 */
export const PreProductionReviewKeySchema = z.object({
  reviewType: ReviewTypeSchema.extract(['pre_production']),
  projectId: ProjectIdSchema,
  projectServiceId: ProjectServiceIdSchema,
})

export type PreProductionReviewKey = ZodSimplify<
  typeof PreProductionReviewKeySchema
>

export const PostProductionReviewKeySchema = z.object({
  reviewType: ReviewTypeSchema.extract(['post_production']),
  projectId: ProjectIdSchema,
})

export type PostProductionReviewKey = ZodSimplify<
  typeof PostProductionReviewKeySchema
>

export const ReviewKeySchema = z.discriminatedUnion('reviewType', [
  PreProductionReviewKeySchema,
  PostProductionReviewKeySchema,
])

export type ReviewKey = ZodSimplify<typeof ReviewKeySchema>

/**
 * ReviewRoundCount
 */
export const ReviewRoundCountSchema = CountSchema
export type ReviewRoundCount = ZodSimplify<typeof ReviewRoundCountSchema>

/**
 * ReviewRoundNumber
 */
export const ReviewRoundNumberSchema = z.number().int().positive()
export type ReviewRoundNumber = ZodSimplify<typeof ReviewRoundNumberSchema>

/**
 * ReviewStatus
 */
export const [ReviewStatuses, ReviewStatusSchema, ReviewStatusEnum] =
  EnumBuilder('concepting', 'reviewing', 'resolving', 'resolved')
export type ReviewStatus = ZodSimplify<typeof ReviewStatusSchema>

/**
 * ReviewDomainSelectModel
 */
export const ReviewDomainSelectModelSchema = z.object({
  reviewId: ReviewIdSchema,
  reviewUrn: ReviewUrnSchema,
  reviewKey: ReviewKeySchema,
  currentRoundNumber: ReviewRoundNumberSchema,
  status: ReviewStatusSchema,
})

export type ReviewDomainSelectModel = ZodSimplify<
  typeof ReviewDomainSelectModelSchema
>

/**
 * ReviewDomainInsertModel
 */
export const ReviewDomainInsertModelSchema = ReviewDomainSelectModelSchema.omit(
  {
    reviewUrn: true,
    status: true, // Always inserted as concepting
    currentRoundNumber: true, // Always inserted as 1
  }
).partial({
  reviewId: true,
})

export type ReviewDomainInsertModel = ZodSimplify<
  typeof ReviewDomainInsertModelSchema
>

/**
 * ReviewDomainUpdateModel
 */
export const ReviewDomainUpdateModelSchema = ReviewDomainSelectModelSchema.pick(
  {
    currentRoundNumber: true,
    status: true,
  }
).partial()

export type ReviewDomainUpdateModel = ZodSimplify<
  typeof ReviewDomainUpdateModelSchema
>

/**
 * ReviewDomainSelectModelFilter
 */
export const ReviewDomainSelectModelFiltersSchema = z
  .object({
    reviewId: ModelFilterSchema(ReviewIdSchema),
    reviewUrn: ModelFilterSchema(ReviewUrnSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectServiceId: ModelFilterSchema(ProjectServiceIdSchema),
    reviewType: ModelFilterSchema(ReviewTypeSchema),
    status: ModelFilterSchema(ReviewStatusSchema),
  })
  .partial()

export type ReviewDomainSelectModelFilters = ZodSimplify<
  typeof ReviewDomainSelectModelFiltersSchema
>
